import { keepPreviousData, useQueries, useQuery } from "@tanstack/react-query";

import { REPORT_CONFIG } from "../../pages/Report/store/utils";

import { AggregationOperation, FieldFilter, GridSort, SummarizeBy } from "../../schemas/report";

import {
	getEmployeeReportDistributionData,
	getGoalReportDistributionData,
	getGridDataCount,
	getRawReviewReportData,
	getReportGridData,
	getReviewReportDistributionData,
	getSurveyReportDistributionData,
} from "../services";

export type ReportVariables = {
	pageNumber: number;
	pageSize: number;
	columns: { id: string; type: string }[];
	filters: FieldFilter[][];
	sorts: GridSort[];
	summarizeBy: SummarizeBy[];
	aggregationOperations: AggregationOperation[];
	questionGroupingSessionID?: string;
};

// TO DO: variables verification. Conditonally call api based on variables.
// In this case we only want to call api if summarizeBy is not empty.
export const useGetRawReviewReportData = (variables, canViewVisualizations = false, reportType = "reviewReport") => {
	const queries = variables.map((variable) => {
		const { summarizeBy } = variable;
		let isQueryEnabled = true;
		if (summarizeBy.length === 0 || !canViewVisualizations) {
			isQueryEnabled = false;
		}
		return {
			queryKey: ["getRawReviewReportData", variable, reportType],
			queryFn: () => getRawReviewReportData(variable, reportType),
			enabled: isQueryEnabled,
		};
	});
	return useQueries({ queries });
};

export const useGetGridDataCount = (variables: ReportVariables, reportType = "reviewReport", enabled = true) => {
	return useQuery({
		queryKey: ["getGridDataCount", variables, reportType],
		queryFn: () => getGridDataCount(variables, reportType),
		enabled,
	});
};

export const useGetReportDistributionData = (variables, reportType = "reviewReport") => {
	const queries = variables.map((variable) => {
		const { summarizeBy, selectedColumnAggregationOperation } = variable;
		let isQueryEnabled = true;
		if (summarizeBy.length === 0 || !selectedColumnAggregationOperation) {
			isQueryEnabled = false;
		}

		if (reportType === "reviewReport") {
			return {
				queryKey: ["getReviewReportDistributionData", variable],
				queryFn: () => getReviewReportDistributionData(variable),
				enabled: isQueryEnabled,
			};
		} else if (reportType === "goalReport") {
			return {
				queryKey: ["getGoalReportDistributionData", variable],
				queryFn: () => getGoalReportDistributionData(variable),
				enabled: isQueryEnabled,
			};
		} else if (reportType === "surveyReport") {
			return {
				queryKey: ["getSurveyReportDistributionData", variable],
				queryFn: () => getSurveyReportDistributionData(variable),
				enabled: isQueryEnabled,
			};
		} else if (reportType === "employeeReport") {
			return {
				queryKey: ["getEmployeeReportDistributionData", variable],
				queryFn: () => getEmployeeReportDistributionData(variable),
				enabled: isQueryEnabled,
			};
		}
	});
	return useQueries({ queries });
};

export const useGetReportGridData = (variables: ReportVariables[], reportType, enabled = true) => {
	const config = reportType ? REPORT_CONFIG[reportType] : null;
	const queryName = config?.queryName;
	const reportsType = config?.reportsType;
	const sorts = config?.sorts;
	const summarizeBy = config?.summarizeBy;
	const aggregationOperations = config?.aggregationOperations;

	const queries = variables.map((variable) => {
		const normalizedVariables = {
			...variable,
			pageNumber:
				variable?.pageNumber !== undefined && variable?.pageNumber !== null
					? variable.pageNumber.toString()
					: undefined,
			pageSize:
				variable?.pageSize !== undefined && variable?.pageSize !== null
					? variable.pageSize.toString()
					: undefined,
		};
		// columns are excluded from the query key so toggling columns doesn't refetch,
		// but are still sent to the server UNLESS in summarized view - then we do refetch data with new columns
		const { columns, ...queryKeyVariables } = normalizedVariables;
		const cachedVariables = variables?.[0]?.summarizeBy?.length > 0 ? normalizedVariables : queryKeyVariables;

		return {
			queryKey: [
				"getReportGridData",
				cachedVariables,
				queryName,
				reportsType,
				sorts,
				summarizeBy,
				aggregationOperations,
			],
			queryFn: () =>
				getReportGridData(
					normalizedVariables,
					queryName,
					reportsType,
					sorts,
					summarizeBy,
					aggregationOperations
				),
			enabled: !!queryName && enabled,
			placeholderData: keepPreviousData,
		};
	});

	return useQueries({ queries });
};
